import { Fragment, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import { ScreenLink as Link } from './permissions/ScreenLink';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ChevronDown, ChevronsUpDown, MoreHorizontal, MoveHorizontal } from 'lucide-react';
import ErrorState from './ErrorState';
import Pagination from './ui/Pagination';
import BulkActionBar, { type BulkAction } from './ui/BulkActionBar';
import type { MenuItemDef } from './ui/Menu';
import { useIsDesktop } from './ui/useOverlay';
import MenuButton, { useTouchMenus } from './table/MenuButton';
import { isInsideTable, MAX_TIP_DEPTH } from '../utils/tableTipScope';
import {
  ariaSortFor, defaultCardSlot, footerCellFor, footerSummaryItems, headerCheckState, nextSort, priorityClass,
  pruneSelection, rowActionLabel, scrollEdges, selectableIds, selectRange, skeletonRowCount, splitRowActions,
  toggleId, togglePage,
  type CardSlot, type ColumnPriority, type ScrollEdges, type SortDir, type SortState,
} from './table/tableModel';

export type { SortState, SortDir, CardSlot, ColumnPriority } from './table/tableModel';
export type { BulkAction } from './ui/BulkActionBar';

/**
 * DataTable v2 (PRD §4.10, T-36; DESIGN_SYSTEM.md §12).
 *
 * - Rows are real links: with `getRowHref`, the identifying column (`link`, or
 *   the first column) renders a `<Link>` whose hit area stretches over the
 *   row, so ⌘/Ctrl/middle-click work. `onRowClick` stays as the v1 fallback.
 * - Sticky header on the tinted `surface-sunken` ground (default when > 20
 *   rows; the whole `<thead>` sticks, so cells with fractional widths leave
 *   no seam), sortable headers with `aria-sort`, compact (40 px) /
 *   comfortable (48 px) rows, a visually hidden caption.
 * - Row actions are contextual (D5): on pointer devices they appear on row
 *   hover / keyboard focus; on touch each row has one 44 px "⋯" (a sheet),
 *   plus any `touchVisible` action (e.g. ▶ on /tasks).
 * - Selection: checkbox column (shown on hover / focus, or while anything is
 *   selected), Shift-range, an indeterminate "select page" header box and a
 *   `BulkActionBar` (only actions with an existing endpoint).
 *   `isRowSelectable` keeps some rows out; `pinnedTop` rows (a running
 *   timer) render above the body and are never selectable.
 * - `footer`: a real `<tfoot>` (totals for exactly the rows shown); on the
 *   phone card list it is a summary block under the cards.
 * - `renderExpanded`: a disclosure button (`aria-expanded`) per row and a
 *   detail row under it.
 * - `mobile="cards"`: below md each row is a full-width card link;
 *   `mobile="scroll"` (default, v1 behaviour) keeps every column, scrolls
 *   sideways with the identifying column sticky on the inline-start side, an
 *   edge fade and a "גללו הצידה" hint on phones. `layout="cards"` shows the
 *   card grid at every width.
 * - Designed loading (skeleton rows, `aria-busy`), empty and error states.
 */

export interface Column<T> {
  key: string;
  /** Header text; also the label in the card list and for screen readers. */
  header: string;
  render: (item: T) => ReactNode;
  /** Extra classes on both the `<th>` and the `<td>` (v1). */
  className?: string;
  /** Numbers, money, hours and dates align to the end (tabular). */
  align?: 'start' | 'end';
  /** 1 always · 2 from lg · 3 from xl (table only; cards and `mobile="scroll"` show everything). */
  priority?: ColumnPriority;
  sortable?: boolean;
  /** First direction when this column is sorted (dates / amounts usually `desc`). */
  sortFirst?: SortDir;
  /** Width class, e.g. `w-32`. */
  width?: string;
  /** Keep the header for screen readers only (an actions column). */
  hideHeader?: boolean;
  /** The identifying column: its content becomes the row link (with `getRowHref`). */
  link?: boolean;
  /** A second line under the identifying cell (not part of the link). */
  sub?: (item: T) => ReactNode;
  /** Small markers on the same line after the link (badges, icons); not part of the link. */
  trail?: (item: T) => ReactNode;
  /** Slot in the card list (default: link → title, priority 3 → details, else meta). */
  card?: CardSlot;
  /** In the card list, prefix the value with the header. */
  cardLabel?: boolean;
}

/** A row action. `inline` actions with an icon show as icon buttons on pointer devices. */
export interface RowAction extends MenuItemDef {
  inline?: boolean;
  /** Only in the touch "⋯" sheet (e.g. the status change, which pointer users do inline). */
  touchOnly?: boolean;
  /** On touch, a visible 44 px icon button next to the "⋯" instead of a sheet item (the row's main job, e.g. ▶). Needs `icon`. */
  touchVisible?: boolean;
}

/** Explicit card content (otherwise built from the columns' `card` slots). */
export interface CardSpec {
  title: ReactNode;
  end?: ReactNode;
  badges?: ReactNode;
  meta?: ReactNode;
  /**
   * The meta line as pieces (preferred over `meta`): joined with "·", each
   * piece bidi-isolated, so Latin names keep their order in RTL. Empty pieces
   * are dropped.
   */
  metaItems?: ReactNode[];
  details?: { label: string; value: ReactNode }[];
}

/** Meta pieces joined by "·", each one isolated (`<bdi>` for text). */
function MetaLine({ items }: { items: ReactNode[] }) {
  const shown = items.filter((m) => !isEmptyNode(m));
  return (
    <>
      {shown.map((m, i) => (
        <span key={i}>
          {isolate(m)}
          {i < shown.length - 1 && <span className="text-text-disabled" aria-hidden="true">{' ·'}</span>}
          {' '}
        </span>
      ))}
    </>
  );
}

export interface DataTablePagination {
  /** 1-based. */
  page: number;
  pageCount: number;
  /** Omit when the visible rows are filtered on the client (the server total would not match). */
  total?: number;
  onChange: (page: number) => void;
}

/** A totals row: a `<tfoot>` in the table, a summary block under the phone cards (hidden in `layout="cards"`). */
export interface DataTableFooter {
  /** Cell per column key; columns without one stay empty. */
  cells: Partial<Record<string, ReactNode>>;
  /** "סה״כ (12)": goes in the first column when it has no cell, and titles the phone summary. */
  label?: ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T;
  isLoading?: boolean;
  /** v1 empty text (used when `emptyState` is not given). */
  emptyMessage?: string;
  /** v1 fallback: a clickable row. Prefer `getRowHref` (a real link). */
  onRowClick?: (item: T) => void;
  getRowHref?: (item: T) => string;
  /** Short name of a row for accessible labels ("בחירת ‹name›", "פעולות: ‹name›"). */
  rowLabel?: (item: T) => string;
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  selection?: ReadonlySet<string>;
  onSelectionChange?: (next: Set<string>) => void;
  /** Rows that may be selected (default: all). The others get no checkbox and stay out of "select page". */
  isRowSelectable?: (item: T) => boolean;
  bulkActions?: BulkAction[];
  rowActions?: (item: T) => RowAction[];
  /** Rows above the body (e.g. a running timer): same columns, links and actions; never selectable, not counted. */
  pinnedTop?: T[];
  /** Totals row (`<tfoot>`); not shown while loading, on error or when empty. */
  footer?: DataTableFooter;
  /** Detail under a row, behind a disclosure button (`aria-expanded`). Without `getRowHref` the whole row toggles. */
  renderExpanded?: (item: T) => ReactNode;
  /** Controlled expanded row ids (otherwise kept inside). */
  expandedKeys?: ReadonlySet<string>;
  onExpandedChange?: (next: Set<string>) => void;
  pagination?: DataTablePagination;
  error?: unknown;
  onRetry?: () => void;
  errorMessage?: string;
  emptyState?: ReactNode;
  /** Default: on when there are more than 20 rows (not in `mobile="scroll"`). */
  stickyHeader?: boolean;
  density?: 'comfortable' | 'compact';
  /** Visually hidden caption that names the table. */
  caption?: string;
  mobile?: 'cards' | 'scroll';
  layout?: 'table' | 'cards';
  card?: (item: T) => CardSpec;
  rowClassName?: (item: T) => string;
  /** Skeleton row count while loading (default 6; use the page size). */
  skeletonRows?: number;
  className?: string;
}

/** D-284: cells give up side padding (was px-4) before the table scrolls, narrower still below xl. */
const CELL_X = 'px-2 xl:px-3';
const TH = `dt-th ${CELL_X} py-2.5 text-xs font-medium text-text-2 whitespace-nowrap bg-surface-sunken`;

function isEmptyNode(node: ReactNode): boolean {
  return node === null || node === undefined || node === false || node === '';
}

/** Plain text and numbers are isolated (`<bdi>`), so a Latin name next to "·" keeps its order in RTL. */
function isolate(node: ReactNode): ReactNode {
  return typeof node === 'string' || typeof node === 'number' ? <bdi>{node}</bdi> : node;
}

function Check({
  checked,
  indeterminate = false,
  label,
  onToggle,
  className = '',
}: {
  checked: boolean;
  indeterminate?: boolean;
  label: string;
  onToggle: (shift: boolean) => void;
  className?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  // The label is the hit area (24 px, 44 px on touch); the box itself stays 16 px.
  return (
    <label className={`dt-check dt-above inline-flex size-6 cursor-pointer items-center justify-center rounded-md touch-target ${className}`}>
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-label={label}
        aria-checked={indeterminate ? 'mixed' : checked}
        onChange={() => { /* handled on click, which carries Shift */ }}
        onClick={(e) => { e.stopPropagation(); onToggle(e.shiftKey); }}
        className="size-4 cursor-pointer rounded-xs accent-primary-600"
      />
    </label>
  );
}

const ICON_BTN = 'inline-flex size-8 items-center justify-center rounded-lg text-text-2 transition-colors duration-micro hover:bg-surface-subtle hover:text-text touch-target';

function RowActions({ actions, rowName, onOpenChange }: { actions: RowAction[]; rowName: string; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const touch = useTouchMenus();
  const { buttons, menu } = splitRowActions(actions, touch);
  if (buttons.length === 0 && menu.length === 0) return null;
  return (
    <div className="dt-above flex items-center justify-end gap-0.5">
      {buttons.map((a) => {
        const text = typeof a.label === 'string' ? a.label : a.textValue ?? '';
        // QA-10c #5: a disabled icon button says WHY (its `hint`, as in the
        // "⋯" menu). A disabled <button> gets no hover in every browser, so
        // the explanation sits on a wrapper and the button lets the pointer
        // through; screen readers get it in the accessible name.
        const why = a.disabled && typeof a.hint === 'string' && a.hint ? a.hint : '';
        const name = a.ariaLabel ?? rowActionLabel(text, rowName);
        const button = (
          <button
            key={a.key}
            type="button"
            onClick={(e) => { e.stopPropagation(); a.onSelect?.(); }}
            disabled={a.disabled}
            aria-label={why ? `${name}. ${why}` : name}
            title={why ? undefined : text || undefined}
            // A touch-visible action is always shown; the others reveal with the row (D5).
            className={`${touch ? '' : 'dt-actions'} ${ICON_BTN} disabled:opacity-50 ${why ? 'pointer-events-none' : ''} ${a.danger ? 'hover:text-danger-700' : ''}`}
          >
            {a.icon}
          </button>
        );
        return why ? (
          <span key={a.key} title={`${text}: ${why}`} className="inline-flex cursor-not-allowed">{button}</span>
        ) : button;
      })}
      {menu.length > 0 && (
        <MenuButton
          items={menu}
          label={t('list.table.rowActions', { name: rowName })}
          align="end"
          onOpenChange={onOpenChange}
          showTooltip
          triggerClassName={`dt-actions ${ICON_BTN} aria-expanded:bg-surface-subtle aria-expanded:text-text`}
        >
          <MoreHorizontal size={18} strokeWidth={1.75} aria-hidden="true" />
        </MenuButton>
      )}
    </div>
  );
}

/** Sideways scroll state of the `mobile="scroll"` frame (overflow, hidden edges). */
function useScrollEdges(ref: RefObject<HTMLDivElement>, enabled: boolean): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>({ overflow: false, start: false, end: false });
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    const update = () => {
      const next = scrollEdges(el.scrollLeft, el.scrollWidth, el.clientWidth);
      setEdges((prev) => (prev.overflow === next.overflow && prev.start === next.start && prev.end === next.end ? prev : next));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', update);
      ro?.disconnect();
    };
  }, [ref, enabled]);
  return edges;
}

/**
 * D-284: a wide table's own scrollbar sits under its last row, often below the
 * fold. A twin bar sticks to the bottom of the viewport while the table is in
 * view and moves the table sideways (and follows it). Returns the scroll width.
 */
function useScrollProxy(frameRef: RefObject<HTMLDivElement>, barRef: RefObject<HTMLDivElement>, active: boolean): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const frame = frameRef.current;
    const bar = barRef.current;
    if (!active || !frame || !bar) return undefined;
    let syncing = false;
    const follow = (from: HTMLElement, to: HTMLElement) => () => {
      if (syncing) { syncing = false; return; }
      if (to.scrollLeft !== from.scrollLeft) { syncing = true; to.scrollLeft = from.scrollLeft; }
    };
    const fromFrame = follow(frame, bar);
    const fromBar = follow(bar, frame);
    // The twin's range equals the frame's (their borders differ).
    const measure = () => { setWidth(frame.scrollWidth - frame.clientWidth + bar.clientWidth); bar.scrollLeft = frame.scrollLeft; };
    measure();
    frame.addEventListener('scroll', fromFrame, { passive: true });
    bar.addEventListener('scroll', fromBar, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(frame);
    if (frame.firstElementChild) ro?.observe(frame.firstElementChild);
    return () => {
      frame.removeEventListener('scroll', fromFrame);
      bar.removeEventListener('scroll', fromBar);
      ro?.disconnect();
    };
  }, [frameRef, barRef, active]);
  return width;
}

/**
 * D-284: text cut with "…" (or clamped) shows in full on hover. Delegated from
 * the table: the first clipped element between the pointer and its cell gets
 * a `title` with its text (an existing title is kept).
 */
function isClipped(el: HTMLElement): boolean {
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1;
}

function titleClippedText(e: { target: EventTarget | null; currentTarget: EventTarget }) {
  let el = e.target instanceof HTMLElement ? e.target : null;
  // FIX-TOOLTIP: events bubbled from a portal (menu, dialog) are not in the table's DOM.
  if (!isInsideTable(el, e.currentTarget as Node)) return;
  // A stretched row link covers the whole row; its title is kept up to date on mouse move.
  if (el?.closest('.dt-link')) return;
  for (let depth = 0; el && el !== e.currentTarget && depth < MAX_TIP_DEPTH; depth++) {
    if (!isInsideTable(el, e.currentTarget as Node)) return;
    const clipped = isClipped(el);
    if (clipped && !el.title && !el.closest('[title]')) {
      const text = el.textContent?.trim();
      if (text) el.title = text;
      return;
    }
    if (el.tagName === 'TD' || el.tagName === 'TH') return;
    el = el.parentElement;
  }
}

/**
 * D-284 (QA): in a row whose link stretches over the whole row the pointer always
 * hits the link, so the clipped text under it is found by position
 * (`elementsFromPoint`) and shown as the LINK's title. A title we set is marked
 * (`data-dt-tip`) and cleared over a cell that is not clipped; a link's own title is never touched.
 * Runs on pointer over/move: React's root listener runs before the shell's AutoTooltip
 * (document), so an icon-only stretched control (the work-logs row toggle) gets
 * `data-no-tooltip` first and the clipped text is not replaced by its aria-label tip.
 */
function titleUnderRowLink(e: { target: EventTarget | null; currentTarget: EventTarget; clientX: number; clientY: number }) {
  const link = e.target instanceof HTMLElement ? e.target.closest<HTMLElement>('.dt-link') : null;
  if (!link || (link.title && link.dataset.dtTip === undefined)) return;
  const frame = e.currentTarget as HTMLElement;
  if (!isInsideTable(link, frame)) return;
  let text = '';
  for (const node of document.elementsFromPoint(e.clientX, e.clientY)) {
    if (!(node instanceof HTMLElement) || !frame.contains(node)) continue;
    if (node.tagName === 'TD' || node.tagName === 'TH' || node.tagName === 'TR') break;
    if (node === link) {
      // The link's own box (its stretched ::after is not part of it): the name cell itself.
      const r = link.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) continue;
    }
    const target = node === link || link.contains(node) ? link : node;
    if (isClipped(target) && target.textContent?.trim()) { text = target.textContent.trim(); break; }
  }
  if (text) {
    if (link.title !== text) link.title = text;
    if (link.dataset.dtTip === undefined) link.dataset.dtTip = link.hasAttribute('data-no-tooltip') ? '' : 'nt';
    if (link.dataset.dtTip === 'nt') link.setAttribute('data-no-tooltip', '');
  } else if (link.dataset.dtTip !== undefined) {
    link.removeAttribute('title');
    if (link.dataset.dtTip === 'nt') link.removeAttribute('data-no-tooltip');
    delete link.dataset.dtTip;
  }
}

const LINK = 'dt-link rounded-xs text-text focus:outline-none';

export default function DataTable<T extends Record<string, any>>({
  columns,
  data,
  keyField,
  isLoading = false,
  emptyMessage,
  onRowClick,
  getRowHref,
  rowLabel,
  sort,
  onSortChange,
  selection,
  onSelectionChange,
  isRowSelectable,
  bulkActions,
  rowActions,
  pinnedTop,
  footer,
  renderExpanded,
  expandedKeys,
  onExpandedChange,
  pagination,
  error,
  onRetry,
  errorMessage,
  emptyState,
  stickyHeader,
  density = 'comfortable',
  caption,
  mobile = 'scroll',
  layout = 'table',
  card,
  rowClassName,
  skeletonRows,
  className = '',
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const baseId = useId();
  const ids = useMemo(() => data.map((item) => String(item[keyField])), [data, keyField]);
  const selIds = useMemo(() => selectableIds(data, ids, isRowSelectable), [data, ids, isRowSelectable]);
  const selectable = !!selection && !!onSelectionChange;
  const selected = useMemo(() => selection ?? new Set<string>(), [selection]);
  const anchor = useRef<string | null>(null);
  const [openMenuRow, setOpenMenuRow] = useState<string | null>(null);
  const [innerExpanded, setInnerExpanded] = useState<Set<string>>(() => new Set());
  const expanded = expandedKeys ?? innerExpanded;
  const expandable = !!renderExpanded;

  // Rows that left the page (or cannot be selected) leave the selection too.
  useEffect(() => {
    if (!selectable || isLoading) return;
    const pruned = pruneSelection(selected, selIds);
    if (pruned !== selected) onSelectionChange?.(new Set(pruned));
  }, [selIds, selectable, selected, isLoading, onSelectionChange]);

  const linkKey = getRowHref ? (columns.find((c) => c.link) ?? columns[0])?.key ?? null : null;
  const identityKey = (columns.find((c) => c.link) ?? columns[0])?.key ?? null;
  const hasActions = !!rowActions;
  const showCards = layout === 'cards' || (mobile === 'cards' && !isDesktop);
  const scroll = mobile === 'scroll' && layout === 'table';
  const compact = density === 'compact';
  const failed = !!error && !isLoading;
  const empty = !isLoading && !failed && data.length === 0;
  const skeletonCount = skeletonRows ?? skeletonRowCount(undefined);
  const labelOf = (item: T) => rowLabel?.(item) ?? String(item[keyField]);
  const pinned = pinnedTop ?? [];
  const showFooter = !!footer && !isLoading && !failed && data.length > 0;

  const scrollRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  // D-284: every table measures its width; one that does not fit scrolls sideways
  // inside its frame (the page never does), with the identifying column sticky.
  const edges = useScrollEdges(scrollRef, !showCards);
  const sideways = scroll || edges.overflow;
  // A sideways-scrolling frame is a scroll container, so a page-sticky head cannot work inside it.
  const sticky = !sideways && (stickyHeader ?? data.length > 20);
  const barWidth = useScrollProxy(scrollRef, barRef, !showCards && edges.overflow);
  // D-284 (Q2): while the table scrolls sideways every cell up to the identifying column sticks
  // (selection / disclosure / a leading column such as the start-work button on /tasks, then the
  // name), so the row can still be acted on mid-scroll. Offsets come from the head row's cell widths.
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const identityIdx = identityKey ? columns.findIndex((c) => c.key === identityKey) : -1;
  const leadCount = (selectable ? 1 : 0) + (expandable ? 1 : 0) + Math.max(identityIdx, 0);
  const [leadOffsets, setLeadOffsets] = useState<number[]>([]);
  useEffect(() => {
    if (!edges.overflow || identityIdx < 0 || !headRowRef.current) return;
    const next: number[] = [];
    let x = 0;
    for (const cell of Array.from(headRowRef.current.children).slice(0, leadCount + 1)) {
      next.push(x);
      x += (cell as HTMLElement).offsetWidth;
    }
    if (next.join() !== leadOffsets.join()) setLeadOffsets(next);
  }, [edges, identityIdx, leadCount, leadOffsets]);

  const toggleRow = (id: string, shift: boolean) => {
    if (!onSelectionChange) return;
    const on = !selected.has(id);
    const next = shift ? selectRange(selected, selIds, anchor.current, id, on) : toggleId(selected, id, on);
    if (!shift) anchor.current = id;
    onSelectionChange(next);
  };
  const headerState = headerCheckState(selected, selIds);
  const canSelect = (item: T) => selectable && (!isRowSelectable || isRowSelectable(item));

  const toggleExpanded = (id: string) => {
    const next = toggleId(expanded, id);
    if (onExpandedChange) onExpandedChange(next);
    if (!expandedKeys) setInnerExpanded(next);
  };
  // Without a row link (or v1 row click) the disclosure button is the row's stretched control.
  const expandStretches = expandable && !getRowHref && !onRowClick;
  const expandId = (id: string) => `${baseId}-x-${id}`;

  const actionsFor = (item: T) => (rowActions ? rowActions(item) : []);

  const errorBlock = (
    <ErrorState message={errorMessage ?? t('list.table.loadError')} onRetry={onRetry} />
  );
  const emptyBlock = emptyState ?? (
    <p className="px-4 py-12 text-center text-sm text-text-2">{emptyMessage || t('common.noDataToDisplay')}</p>
  );

  const bulk = selectable && bulkActions && bulkActions.length > 0 && selected.size > 0 ? (
    <BulkActionBar
      className="mb-3"
      count={selected.size}
      actions={bulkActions}
      onClear={() => { anchor.current = null; onSelectionChange?.(new Set()); }}
    />
  ) : null;

  /* ---------------- Card list (mobile cards, or layout="cards") ---------------- */
  if (showCards) {
    const grid = layout === 'cards';
    const cardFor = (item: T): CardSpec => {
      if (card) return card(item);
      const spec: CardSpec = { title: null };
      const metas: ReactNode[] = [];
      const badges: ReactNode[] = [];
      const details: { label: string; value: ReactNode }[] = [];
      for (const col of columns) {
        const slot = defaultCardSlot(col, linkKey);
        if (slot === 'none') continue;
        const value = col.render(item);
        if (slot === 'title') { spec.title = value; continue; }
        if (isEmptyNode(value)) continue;
        const labelled = col.cardLabel ? <>{col.header}: {isolate(value)}</> : isolate(value);
        if (slot === 'end' && !spec.end) spec.end = value;
        else if (slot === 'badges') badges.push(<Fragment key={col.key}>{value}</Fragment>);
        else if (slot === 'details') details.push({ label: col.header, value });
        else metas.push(<Fragment key={col.key}>{labelled}</Fragment>);
      }
      if (badges.length) spec.badges = badges;
      if (metas.length) spec.metaItems = metas;
      if (details.length) spec.details = details;
      return spec;
    };

    const listClass = grid
      ? 'grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3'
      : 'divide-y divide-border';

    const renderCard = (item: T, id: string, isPinned: boolean) => {
      const spec = cardFor(item);
      const metaLine = spec.metaItems?.some((m) => !isEmptyNode(m)) ? <MetaLine items={spec.metaItems} /> : spec.meta;
      const href = getRowHref?.(item);
      const actions = actionsFor(item);
      const isSel = !isPinned && selected.has(id);
      const withCheck = !isPinned && canSelect(item);
      const canExpand = expandable && !isPinned;
      const isOpen = canExpand && expanded.has(id);
      const rowKey = isPinned ? `pin-${id}` : id;
      return (
        <li
          key={rowKey}
          className={`dt-row relative isolate transition-colors duration-micro ${grid ? 'rounded-xl border border-border bg-surface p-4 hover:shadow-card' : 'min-h-16 px-4 py-3'} ${isSel ? 'bg-accent-soft' : ''} ${rowClassName?.(item) ?? ''}`}
          data-selected={isSel || undefined}
          data-pinned={isPinned || undefined}
          data-menu-open={openMenuRow === rowKey || undefined}
        >
          <div className="flex items-start gap-3">
            {withCheck && (
              <Check checked={isSel} label={t('list.table.selectRow', { name: labelOf(item) })} onToggle={(shift) => toggleRow(id, shift)} className="-ms-1 mt-0.5" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1 text-sm font-medium leading-snug text-text">
                  {href ? (
                    <Link to={href} className={`${LINK} line-clamp-2 break-words`}>{isolate(spec.title)}</Link>
                  ) : (
                    <span className="line-clamp-2 break-words">{isolate(spec.title)}</span>
                  )}
                </div>
                {!isEmptyNode(spec.end) && <div className="shrink-0 pt-0.5 text-xs text-text-2 tabular-nums">{spec.end}</div>}
              </div>
              {spec.badges && <div className="mt-1.5 flex flex-wrap items-center gap-1.5">{spec.badges}</div>}
              {!isEmptyNode(metaLine) && <div className="mt-1 min-w-0 text-xs text-text-2 break-words">{metaLine}</div>}
              {spec.details && spec.details.length > 0 && (
                <details className="dt-above mt-2 text-xs">
                  <summary className="inline-flex min-h-touch cursor-pointer items-center text-accent-text md:min-h-0">{t('list.table.moreDetails')}</summary>
                  <dl className="mt-1 grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
                    {spec.details.map((d) => (
                      <Fragment key={d.label}>
                        <dt className="text-text-3">{d.label}</dt>
                        <dd className="text-text">{isolate(d.value)}</dd>
                      </Fragment>
                    ))}
                  </dl>
                </details>
              )}
              {canExpand && (
                <button
                  type="button"
                  onClick={() => toggleExpanded(id)}
                  aria-expanded={isOpen}
                  aria-controls={isOpen ? expandId(id) : undefined}
                  aria-label={t('list.table.rowDetails', { name: labelOf(item) })}
                  data-dt-expand=""
                  className={`${expandStretches ? 'dt-link focus:outline-none' : 'dt-above'} mt-1 inline-flex min-h-touch items-center gap-1 rounded-xs text-xs text-accent-text md:min-h-0`}
                >
                  {t('list.table.showDetails')}
                  <ChevronDown size={14} aria-hidden="true" className={`transition-transform duration-micro ${isOpen ? 'rotate-180' : ''}`} />
                </button>
              )}
              {isOpen && (
                <div id={expandId(id)} className="dt-above mt-2 text-sm">{renderExpanded?.(item)}</div>
              )}
            </div>
            {actions.length > 0 && (
              <div className="-me-2 -mt-1.5 shrink-0">
                <RowActions
                  actions={actions}
                  rowName={labelOf(item)}
                  onOpenChange={(o) => setOpenMenuRow(o ? rowKey : null)}
                />
              </div>
            )}
          </div>
        </li>
      );
    };

    const pinnedCards = pinned.length > 0 ? (
      <ul className={listClass}>
        {pinned.map((item) => renderCard(item, String(item[keyField]), true))}
      </ul>
    ) : null;

    let body: ReactNode;
    if (isLoading) {
      body = (
        <ul className={listClass} aria-hidden="true">
          {Array.from({ length: skeletonCount }, (_, i) => (
            <li key={i} className={`${grid ? 'rounded-xl border border-border bg-surface p-4' : 'px-4 py-3'} min-h-16 animate-pulse`}>
              <div className="h-3.5 rounded-xs bg-surface-subtle" style={{ width: `${60 + ((i * 13) % 30)}%` }} />
              <div className="mt-2.5 h-3 w-2/5 rounded-xs bg-surface-subtle" />
            </li>
          ))}
        </ul>
      );
    } else if (failed) {
      body = errorBlock;
    } else if (empty) {
      body = emptyBlock;
    } else {
      body = (
        <ul className={listClass} aria-label={caption}>
          {data.map((item, idx) => renderCard(item, ids[idx], false))}
        </ul>
      );
    }

    // The totals as a summary block under the phone cards (not in the card grid).
    const summaryItems = showFooter && !grid && footer ? footerSummaryItems(footer, columns) : [];
    const summary = showFooter && !grid && footer ? (
      <section className="border-t border-border bg-surface-sunken px-4 py-3" aria-label={typeof footer.label === 'string' ? footer.label : t('list.table.totals')}>
        {!isEmptyNode(footer.label) && <p className="text-sm font-semibold text-text">{footer.label}</p>}
        {summaryItems.length > 0 && (
          <dl className="mt-1.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-sm">
            {summaryItems.map((s) => (
              <Fragment key={s.key}>
                <dt className="text-text-2">{s.label}</dt>
                <dd className="text-end font-medium text-text tabular-nums">{s.value}</dd>
              </Fragment>
            ))}
          </dl>
        )}
      </section>
    ) : null;

    return (
      <div className={className}>
        {bulk}
        {isLoading && <p className="sr-only" role="status">{t('list.table.loading')}</p>}
        {grid && !failed && !empty ? <>{pinnedCards && <div className="mb-3">{pinnedCards}</div>}{body}</> : (
          <div className="dt relative overflow-clip rounded-xl border border-border bg-surface" data-selecting={selected.size > 0 || undefined} aria-busy={isLoading || undefined}>
            {pinnedCards && <div className="border-b border-border">{pinnedCards}</div>}
            {body}
            {summary}
          </div>
        )}
        {pagination && !isLoading && !failed && (
          <Pagination className="mt-4" page={pagination.page} pageCount={pagination.pageCount} total={pagination.total} onChange={pagination.onChange} />
        )}
      </div>
    );
  }

  /* ---------------- Table ---------------- */
  const colCount = columns.length + (selectable ? 1 : 0) + (expandable ? 1 : 0) + (hasActions ? 1 : 0);
  const rowH = compact ? 'h-10' : 'h-12';
  // Row height comes from the tr (h-10 / h-12); the 32 px row controls fit inside it.
  const cellY = compact ? 'py-1' : 'py-2';
  // The identifying column stays put while a wide table scrolls sideways.
  const stickyCol = edges.overflow;
  const lead = selectable ? 1 : 0;
  /** Position of a data column in the row (after the selection and disclosure cells). */
  const colPos = (i: number) => lead + (expandable ? 1 : 0) + i;
  /** Sticky class + inline-start offset for the cell at `pos` (D-284 Q2: every cell up to the name column). */
  const leadSticky = (pos: number): { cls: string; style?: CSSProperties } => {
    if (!stickyCol || identityIdx < 0 || pos > leadCount) return { cls: '' };
    const edge = pos === leadCount && edges.start ? 'after:pointer-events-none after:absolute after:inset-y-0 after:end-0 after:w-px after:bg-border' : '';
    return { cls: `sticky z-sticky ${edge}`, style: { insetInlineStart: leadOffsets[pos] ?? 0 } };
  };
  const selSticky = leadSticky(0);
  const expSticky = leadSticky(lead);
  // D-284: no column is hidden by screen width; a table that does not fit scrolls instead.
  const keepAll = true;

  const headerCell = (col: Column<T>, i: number) => {
    const alignClass = col.align === 'end' ? 'text-end' : 'text-start';
    const st = leadSticky(colPos(i));
    const classes = `${TH} ${alignClass} ${priorityClass(col.priority, keepAll)} ${col.width ?? ''} ${st.cls} ${col.className ?? ''}`;
    const label = col.hideHeader || !col.header ? <span className="sr-only">{col.header || t('list.table.actions')}</span> : col.header;
    if (!col.sortable || !onSortChange) {
      return <th key={col.key} scope="col" className={classes} style={st.style}>{label}</th>;
    }
    const aria = ariaSortFor(sort, col.key);
    const Icon = aria === 'none' ? ChevronsUpDown : aria === 'ascending' ? ArrowUp : ArrowDown;
    return (
      <th key={col.key} scope="col" aria-sort={aria} className={classes} style={st.style}>
        <button
          type="button"
          onClick={() => onSortChange(nextSort(sort, col.key, col.sortFirst))}
          // C-22 (clarity, B-16): a 24 px target (the header text alone was 16 px).
          className={`group -mx-1 inline-flex min-h-6 items-center gap-1 rounded-md px-1 py-0.5 transition-colors duration-micro hover:text-text ${aria !== 'none' ? 'text-text' : ''} ${col.align === 'end' ? 'flex-row-reverse' : ''}`}
        >
          {col.header}
          <Icon
            size={12}
            aria-hidden="true"
            className={aria !== 'none' ? 'text-text-2' : 'text-text-disabled opacity-0 transition-opacity duration-micro group-hover:opacity-100 group-focus-visible:opacity-100'}
          />
        </button>
      </th>
    );
  };

  const v1Clickable = !getRowHref && !!onRowClick;

  const renderRow = (item: T, id: string, isPinned: boolean) => {
    const href = getRowHref?.(item);
    const actions = actionsFor(item);
    const isSel = !isPinned && selected.has(id);
    const canExpand = expandable && !isPinned;
    const isOpen = canExpand && expanded.has(id);
    const rowKey = isPinned ? `pin-${id}` : id;
    // In a sideways-scrolling table the row has a solid ground, so the sticky cell can inherit it.
    const ground = isSel ? 'bg-accent-soft' : `${sideways ? 'bg-surface' : ''} hover:bg-surface-subtle`;
    const row = (
      <tr
        key={rowKey}
        className={`dt-row relative isolate ${rowH} border-b border-border/70 ${isOpen ? '' : 'last:border-b-0'} transition-colors duration-micro ${ground} ${v1Clickable ? 'cursor-pointer focus-visible:bg-surface-subtle' : ''} ${rowClassName?.(item) ?? ''}`}
        data-selected={isSel || undefined}
        data-pinned={isPinned || undefined}
        data-expanded={isOpen || undefined}
        data-menu-open={openMenuRow === rowKey || undefined}
        {...(v1Clickable ? {
          onClick: () => onRowClick?.(item),
          onKeyDown: (e: ReactKeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick?.(item); } },
          tabIndex: 0,
        } : {})}
      >
        {selectable && (
          <td className={`ps-4 pe-0 ${cellY} align-middle ${selSticky.cls ? `${selSticky.cls} bg-inherit` : ''}`} style={selSticky.style}>
            {!isPinned && canSelect(item) && (
              <Check checked={isSel} label={t('list.table.selectRow', { name: labelOf(item) })} onToggle={(shift) => toggleRow(id, shift)} className="-ms-1" />
            )}
          </td>
        )}
        {expandable && (
          <td className={`ps-2 pe-0 ${cellY} align-middle ${expSticky.cls ? `${expSticky.cls} bg-inherit` : ''}`} style={expSticky.style}>
            {canExpand && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleExpanded(id); }}
                aria-expanded={isOpen}
                aria-controls={isOpen ? expandId(id) : undefined}
                aria-label={t('list.table.rowDetails', { name: labelOf(item) })}
                data-dt-expand=""
                className={`${expandStretches ? 'dt-link focus:outline-none' : 'dt-above'} ${ICON_BTN}`}
              >
                <ChevronDown
                  size={16}
                  strokeWidth={1.75}
                  aria-hidden="true"
                  className={`transition-transform duration-micro ${isOpen ? '' : 'ltr:-rotate-90 rtl:rotate-90'}`}
                />
              </button>
            )}
          </td>
        )}
        {columns.map((col, i) => {
          const alignClass = col.align === 'end' ? 'text-end tabular-nums' : 'text-start';
          const isLink = !!href && col.key === linkKey;
          const content = col.render(item);
          // The identifying cell takes the free width (truncating); in a scrolling table it keeps a readable width and wraps.
          // D-284: the truncating cell keeps a readable minimum; past that the table scrolls.
          const sizing = isLink ? (scroll ? 'min-w-40 max-w-xs' : 'min-w-40 max-w-0 w-full') : '';
          const stickyCell = leadSticky(colPos(i));
          return (
            <td
              key={col.key}
              className={`${CELL_X} ${cellY} align-middle text-text ${alignClass} ${priorityClass(col.priority, keepAll)} ${col.width ?? ''} ${sizing} ${stickyCell.cls ? `${stickyCell.cls} bg-inherit` : ''} ${col.className ?? ''}`}
              style={stickyCell.style}
            >
              {isLink ? (
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-2">
                    <Link to={href} className={`${LINK} block min-w-0 font-medium ${scroll ? 'line-clamp-2 break-words' : 'truncate'}`}>{content}</Link>
                    {col.trail && <span className="dt-above flex shrink-0 items-center gap-1.5">{col.trail(item)}</span>}
                  </div>
                  {col.sub && <div className={`mt-0.5 min-w-0 text-xs text-text-2 ${scroll ? '' : 'truncate'}`}>{col.sub(item)}</div>}
                </div>
              ) : (
                <>
                  {col.trail ? (
                    // QA-4 #2: the trail (a badge such as "⚠️ חסרה עלות") shows on EVERY row, not only on link rows.
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="min-w-0">{content}</span>
                      <span className="dt-above flex shrink-0 items-center gap-1.5">{col.trail(item)}</span>
                    </div>
                  ) : content}
                  {col.sub && <div className="mt-0.5 text-xs text-text-2">{col.sub(item)}</div>}
                </>
              )}
            </td>
          );
        })}
        {hasActions && (
          <td className={`pe-2 ps-0 ${cellY} align-middle`}>
            {actions.length > 0 && (
              <RowActions
                actions={actions}
                rowName={labelOf(item)}
                onOpenChange={(o) => setOpenMenuRow(o ? rowKey : null)}
              />
            )}
          </td>
        )}
      </tr>
    );
    if (!isOpen) return row;
    return (
      <Fragment key={rowKey}>
        {row}
        <tr id={expandId(id)} className="dt-expanded border-b border-border/70 bg-surface-subtle last:border-b-0">
          <td colSpan={colCount} className={`py-3 pe-4 ${selectable ? 'ps-20' : 'ps-12'}`}>
            {renderExpanded?.(item)}
          </td>
        </tr>
      </Fragment>
    );
  };

  const frame = (
    <div
      ref={scrollRef}
      className={`dt relative rounded-xl border border-border bg-surface ${sideways ? 'dt-scroll overflow-x-auto' : 'overflow-clip'} ${edges.overflow ? 'dt-scroll-twin' : ''}`}
      data-selecting={selected.size > 0 || undefined}
      // A sideways-scrolling region is reachable and named for keyboard and screen-reader users.
      {...(edges.overflow ? { role: 'region', tabIndex: 0, 'aria-label': caption ?? t('list.table.scrollRegion') } : {})}
      onMouseOver={titleClippedText}
      onFocus={titleClippedText}
      onPointerOver={titleUnderRowLink}
      onPointerMove={titleUnderRowLink}
    >
      <table className="w-full text-sm" aria-busy={isLoading || undefined}>
        {caption && <caption className="sr-only">{caption}</caption>}
        {/* The whole head sticks (not each th), on one solid ground: no seams between cells with fractional widths. */}
        <thead className={sticky ? 'sticky top-chrome z-sticky' : ''}>
          <tr ref={headRowRef} className="bg-surface-sunken">
            {selectable && (
              <th scope="col" className={`${TH} w-10 pe-0 text-start ${selSticky.cls}`} style={selSticky.style}>
                {selIds.length > 0 && (
                  <Check
                    checked={headerState === 'all'}
                    indeterminate={headerState === 'some'}
                    label={t('list.table.selectPage')}
                    onToggle={() => { anchor.current = null; onSelectionChange?.(togglePage(selected, selIds)); }}
                    className="dt-check-head -ms-1"
                  />
                )}
              </th>
            )}
            {expandable && (
              <th scope="col" className={`${TH} w-10 pe-0 ${expSticky.cls}`} style={expSticky.style}><span className="sr-only">{t('list.table.details')}</span></th>
            )}
            {columns.map(headerCell)}
            {hasActions && (
              <th scope="col" className={`${TH} w-px`}><span className="sr-only">{t('list.table.actions')}</span></th>
            )}
          </tr>
        </thead>
        {pinned.length > 0 && (
          <tbody className="border-b border-border">
            {pinned.map((item) => renderRow(item, String(item[keyField]), true))}
          </tbody>
        )}
        <tbody>
          {isLoading && (
            <>
              {Array.from({ length: skeletonCount }, (_, i) => (
                <tr key={`sk-${i}`} className={`${rowH} border-b border-border/70 last:border-b-0`} aria-hidden="true">
                  {Array.from({ length: colCount }, (__, j) => {
                    const c = j - lead - (expandable ? 1 : 0);
                    const col = c >= 0 && c < columns.length ? columns[c] : undefined;
                    return (
                      <td key={j} className={`${CELL_X} ${cellY} ${col ? priorityClass(col.priority, keepAll) : ''}`}>
                        {col && (
                          <div
                            className="h-3 animate-pulse rounded-xs bg-surface-subtle"
                            style={{ width: c === 0 ? `${55 + ((i * 17) % 35)}%` : `${40 + ((i * 7 + j * 11) % 40)}%` }}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              <tr className="sr-only"><td colSpan={colCount}><span role="status">{t('list.table.loading')}</span></td></tr>
            </>
          )}
          {failed && <tr><td colSpan={colCount}>{errorBlock}</td></tr>}
          {empty && <tr><td colSpan={colCount}>{emptyBlock}</td></tr>}
          {!isLoading && !failed && data.map((item, idx) => renderRow(item, ids[idx], false))}
        </tbody>
        {showFooter && footer && (
          <tfoot>
            <tr className={`${rowH} border-t border-border bg-surface-sunken font-semibold`}>
              {selectable && <td className={selSticky.cls ? `${selSticky.cls} bg-inherit` : ''} style={selSticky.style} />}
              {expandable && <td className={expSticky.cls ? `${expSticky.cls} bg-inherit` : ''} style={expSticky.style} />}
              {columns.map((col, i) => {
                const value = footerCellFor(footer, col.key, i === 0);
                const alignClass = col.align === 'end' ? 'text-end tabular-nums' : 'text-start';
                const st = leadSticky(colPos(i));
                const cls = `${CELL_X} ${cellY} align-middle text-text ${alignClass} ${priorityClass(col.priority, keepAll)} ${col.width ?? ''} ${st.cls ? `${st.cls} bg-inherit` : ''} ${col.className ?? ''}`;
                // The label cell names the row for screen readers.
                return i === 0
                  ? <th key={col.key} scope="row" className={`${cls} whitespace-nowrap`} style={st.style}>{value}</th>
                  : <td key={col.key} className={cls} style={st.style}>{value}</td>;
              })}
              {hasActions && <td />}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );

  return (
    <div className={className}>
      {bulk}
      {/* One structure for every table (D-284): a frame that starts or stops scrolling is not remounted. */}
        <>
          {/* Phones: say once that the table scrolls (only when it does). */}
          {edges.overflow && (
            <p className="mb-2 flex items-center gap-1.5 text-xs text-text-2 md:hidden">
              <MoveHorizontal size={14} aria-hidden="true" />
              {t('list.table.scrollHint')}
            </p>
          )}
          <div className="relative">
            {frame}
            {/* Edge fade on the side with more columns. */}
            <div
              aria-hidden="true"
              className={`pointer-events-none absolute inset-y-px end-px w-10 rounded-e-xl from-surface to-transparent transition-opacity duration-micro ltr:bg-gradient-to-l rtl:bg-gradient-to-r ${edges.end ? 'opacity-100' : 'opacity-0'}`}
            />
          </div>
          {/* D-284: a visible scrollbar at the bottom of the screen while the table is in view (pointer screens). */}
          {edges.overflow && (
            <div ref={barRef} className="dt-scroll-bar sticky bottom-0 z-sticky hidden overflow-x-auto bg-surface md:block" aria-hidden="true" data-dt-scroll-bar="">
              <div className="h-px" style={{ width: barWidth }} />
            </div>
          )}
        </>
      {pagination && !isLoading && !failed && (
        <Pagination className="mt-4" page={pagination.page} pageCount={pagination.pageCount} total={pagination.total} onChange={pagination.onChange} />
      )}
    </div>
  );
}
